<template>
  <div>
    <!-- 统计行 -->
    <a-row :gutter="24">
      <a-col :span="24" :lg="6" :md="12" class="mb-24" v-for="s in stats" :key="s.title">
        <WidgetCounter :title="s.title" :value="s.value" :prefix="s.prefix" :suffix="s.suffix"
          :icon="s.icon" :status="s.status"></WidgetCounter>
      </a-col>
    </a-row>

    <!-- 主表格卡片 -->
    <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ padding: 0 }">
      <template #title>
        <a-row type="flex" align="middle" :gutter="[16, 12]">
          <a-col :span="24" :md="8">
            <h5 class="font-semibold m-0">域名管理</h5>
            <p class="text-muted m-0" style="font-size:13px">接入 Cloudflare 隐藏源站 IP · 迁移建 zone 对线上零影响</p>
          </a-col>
          <a-col :span="24" :md="16" class="dm-toolbar">
            <a-input-search v-model="newDomain" placeholder="单个域名,如 example.com"
              enter-button="迁移到 CF" :loading="migrating" style="max-width:300px"
              @search="doMigrate(newDomain)" />
            <a-button type="primary" icon="cloud-upload" @click="batchVisible = true">批量添加</a-button>
            <a-button shape="circle" icon="reload" @click="load" />
          </a-col>
        </a-row>
      </template>

      <a-alert type="info" show-icon banner class="dm-ns-bar"
        :message="'迁移后到注册商把 NS 改为:  ' + nsHint" />

      <a-alert v-if="warnCount > 0" type="error" show-icon banner class="dm-ns-bar dm-warn-bar">
        <template #message>
          <span>检测到 <b>{{ warnCount }}</b> 个域名连续不可达(疑似到期 / 源站故障),建议核查后删除。</span>
          <a-button size="small" type="link" @click="onlyWarn = true; filterStatus=''; filterCf=''; filterValid=''; search=''">只看这些</a-button>
          <a-button v-if="onlyWarn" size="small" type="link" @click="onlyWarn = false">显示全部</a-button>
        </template>
      </a-alert>

      <!-- 筛选栏 -->
      <div class="dm-filters">
        <a-input v-model="search" placeholder="搜索域名 / 归属" allow-clear style="width:220px">
          <a-icon slot="prefix" type="search" />
        </a-input>
        <a-select v-model="filterStatus" style="width:130px" placeholder="状态">
          <a-select-option value="">全部状态</a-select-option>
          <a-select-option value="pool">未分配(池)</a-select-option>
          <a-select-option value="assigned">已分配</a-select-option>
          <a-select-option value="pending_ns">待改NS</a-select-option>
          <a-select-option value="active">已上线</a-select-option>
          <a-select-option value="orphan">待清理</a-select-option>
        </a-select>
        <a-select v-model="filterCf" style="width:130px" placeholder="CF状态">
          <a-select-option value="">全部CF</a-select-option>
          <a-select-option value="ready">已生效</a-select-option>
          <a-select-option value="pending">待NS</a-select-option>
          <a-select-option value="none">未接入</a-select-option>
        </a-select>
        <a-select v-model="filterValid" style="width:140px" placeholder="归属状态">
          <a-select-option value="">全部归属</a-select-option>
          <a-select-option value="valid">客户有效</a-select-option>
          <a-select-option value="expired">客户已过期</a-select-option>
          <a-select-option value="unassigned">未分配</a-select-option>
        </a-select>
        <a-button v-if="search || filterStatus || filterCf || filterValid || onlyWarn" size="small" type="link" @click="clearFilters">清空筛选</a-button>
        <span class="dm-count">筛选出 {{ displayItems.length }} / {{ items.length }} 个</span>
      </div>

      <a-table
        :columns="columns"
        :data-source="displayItems"
        :loading="loading"
        row-key="domain"
        :row-class-name="rowClass"
        :pagination="{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }"
        size="middle" class="dm-table">
        <template slot="domainCell" slot-scope="text, record">
          <div class="dm-domain">
            <span class="dm-dot" :class="'dot-' + record.status"></span>
            <span class="dm-name">{{ record.domain }}</span>
          </div>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag class="tag-status" :class="'st-' + text">{{ statusLabel(text) }}</a-tag>
        </template>
        <template slot="owner" slot-scope="text, record">
          <span v-if="record.owner" class="dm-owner">{{ record.owner }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template slot="cf" slot-scope="text, record">
          <a-badge v-if="record.cf_ready === '1'" status="success" text="已生效" />
          <a-badge v-else-if="record.cf_zone_id" status="warning" text="待NS" />
          <a-badge v-else status="default" text="未接入" />
        </template>
        <template slot="valid" slot-scope="text, record">
          <a-tooltip v-if="record.valid_state === 'valid'" :title="'归属客户:' + (record.owner || '-') + ' · ' + ttlText(record.owner_ttl)">
            <a-tag color="green">客户有效</a-tag>
          </a-tooltip>
          <a-tooltip v-else-if="record.valid_state === 'expired'" :title="'归属客户 ' + (record.owner || '') + ' 的账号已过期。这不代表域名不可用 —— 域名本身是否可达请看「健康」列。'">
            <a-tag color="red">客户已过期</a-tag>
          </a-tooltip>
          <a-tag v-else color="default">未分配</a-tag>
        </template>
        <template slot="health" slot-scope="text, record">
          <a-tooltip v-if="record.health_warn == 1"
            :title="'连续不可达(HTTP ' + (record.health_code || 0) + ') · ' + (record.health_error || '') + ' · 最近检测 ' + (record.health_checked_at || '')">
            <a-tag color="red"><a-icon type="warning" /> 疑似到期</a-tag>
          </a-tooltip>
          <a-tooltip v-else-if="record.health === 'down'" :title="'首次不可达(HTTP ' + (record.health_code || 0) + '),复检确认中 · ' + (record.health_checked_at || '')">
            <a-tag color="orange">异常重试中</a-tag>
          </a-tooltip>
          <a-tooltip v-else-if="record.health === 'ok'" :title="'HTTP ' + (record.health_code || 200) + ' · 最近检测 ' + (record.health_checked_at || '')">
            <a-tag color="green">正常</a-tag>
          </a-tooltip>
          <span v-else class="text-muted">未检测</span>
        </template>
        <template slot="ns" slot-scope="text, record">
          <span v-if="record.cf_ns" class="dm-ns">{{ record.cf_ns }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-button v-if="!record.cf_zone_id" size="small" type="primary" ghost @click="doMigrate(record.domain)">迁移CF</a-button>
          <a-button v-else size="small" @click="doRefresh(record.domain)">刷新</a-button>
          <a-button v-if="record.cf_ready === '1'" size="small" type="link" :loading="checking === record.domain" @click="doHealthCheck(record.domain)">检测</a-button>
          <a-button size="small" type="link" @click="openAssign(record)">分配</a-button>
          <a-button v-if="record.owner" size="small" type="link" @click="doUnassign(record.domain)">取消</a-button>
          <a-dropdown :trigger="['click']">
            <a-button size="small" type="link" class="dm-del">删除 <a-icon type="down" /></a-button>
            <a-menu slot="overlay">
              <a-menu-item @click="doRemove(record, false)">
                <span>仅删本地(保留 CF zone)</span>
              </a-menu-item>
              <a-menu-item @click="doRemove(record, true)" class="dm-del">
                <span>彻底删除(含 CF zone)</span>
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </template>
      </a-table>
    </a-card>

    <!-- 批量添加弹窗 -->
    <a-modal v-model="batchVisible" title="批量添加域名" :confirmLoading="batching" ok-text="开始批量建站+接入CF"
      cancel-text="取消" @ok="doBatch" width="640px">
      <a-form-item label="域名列表(每行一个,或逗号/空格分隔)" :colon="false">
        <a-textarea v-model="batchText" :rows="8" placeholder="example1.com&#10;example2.com&#10;example3.com" />
      </a-form-item>
      <a-form-item label="同时分配给用户(可选,留空=进未分配池)" :colon="false">
        <a-input v-model="batchOwner" placeholder="客户用户名(须已存在),如 @xxx 或 aegisxxxx" />
      </a-form-item>
      <a-alert type="info" show-icon
        message="全新域名会自动建源站;已有目录的域名只接入 CF。提交后后台异步处理,几分钟后刷新列表。" />
      <div v-if="batchResult" style="margin-top:12px">
        <a-alert type="success" show-icon
          :message="'已受理 ' + batchResult.accepted_count + ' 个,NS 统一改为:' + batchResult.name_servers.join(' / ')" />
        <p v-if="batchResult.invalid && batchResult.invalid.length" class="text-muted" style="margin-top:8px">
          已跳过无效域名:{{ batchResult.invalid.join(', ') }}
        </p>
      </div>
    </a-modal>

    <a-modal v-model="assignVisible" title="分配域名给客户" @ok="doAssign" :confirmLoading="assigning" ok-text="确认分配" cancel-text="取消">
      <p>域名:<a-tag color="blue">{{ assignDomain }}</a-tag></p>
      <a-input v-model="assignOwner" placeholder="客户用户名(须已存在),如 @xxx 或 aegisxxxx" />
    </a-modal>

    <a-modal v-model="nsVisible" title="迁移成功 — 请去注册商改 NS" :footer="null" class="dm-ns-modal">
      <a-result status="success" :title="nsDomain + ' 已在 Cloudflare 建好 zone'"
        sub-title="把该域名在注册商处的 Nameservers 改为下面两条:">
        <template #extra>
          <div class="dm-ns-list">
            <a-tag v-for="ns in nsList" :key="ns" color="geekblue" class="dm-ns-item">{{ ns }}</a-tag>
          </div>
          <p class="text-muted" style="margin-top:14px">改完后点该域名"刷新",生效后即走 CF、隐藏源站 IP。</p>
        </template>
      </a-result>
    </a-modal>
  </div>
</template>

<script>
import { dmList, dmMigrate, dmBatchMigrate, dmRefresh, dmAssign, dmUnassign, dmRemove, dmHealthCheck } from '../../api/index';
import WidgetCounter from '../../components/Widgets/WidgetCounter';

const IC = {
  total: `<svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm5.94 7h-2.8c-.08-1.76-.34-3.34-.76-4.64A6.02 6.02 0 0 1 15.94 9ZM10 4c.64 0 1.5 1.94 1.14 5H8.86C8.5 5.94 9.36 4 10 4ZM7.62 4.36C7.2 5.66 6.94 7.24 6.86 9H4.06a6.02 6.02 0 0 1 3.56-4.64ZM4.06 11h2.8c.08 1.76.34 3.34.76 4.64A6.02 6.02 0 0 1 4.06 11ZM10 16c-.64 0-1.5-1.94-1.14-5h2.28C11.5 14.06 10.64 16 10 16Zm2.38-.36c.42-1.3.68-2.88.76-4.64h2.8a6.02 6.02 0 0 1-3.56 4.64Z" fill="#fff"/></svg>`,
  cf: `<svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12a4 4 0 0 1 .5-7.97A5 5 0 0 1 14.9 6H15a3 3 0 0 1 .5 5.96" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/><path d="m8 12 2 2 4-4" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  pending: `<svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="8" stroke="#fff" stroke-width="1.6"/><path d="M10 6v4l2.5 2" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>`,
  pool: `<svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6h12M4 10h12M4 14h12" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>`,
};

export default {
  components: { WidgetCounter },
  data() {
    return {
      loading: false, migrating: false, assigning: false, checking: '',
      items: [], newDomain: '', filterStatus: '', filterCf: '', filterValid: '', search: '', onlyWarn: false,
      nsHint: 'irena.ns.cloudflare.com   /   josh.ns.cloudflare.com',
      assignVisible: false, assignDomain: '', assignOwner: '',
      nsVisible: false, nsDomain: '', nsList: [],
      batchVisible: false, batching: false, batchText: '', batchOwner: '', batchResult: null,
      columns: [
        { title: '域名', key: 'domain', scopedSlots: { customRender: 'domainCell' } },
        { title: '状态', dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' }, width: 100 },
        { title: '归属', dataIndex: 'owner', key: 'owner', scopedSlots: { customRender: 'owner' }, width: 140 },
        { title: 'CF', key: 'cf', scopedSlots: { customRender: 'cf' }, width: 100 },
        { title: '归属状态', key: 'valid', scopedSlots: { customRender: 'valid' }, width: 120 },
        { title: '健康', key: 'health', scopedSlots: { customRender: 'health' }, width: 150 },
        { title: 'Nameservers', key: 'ns', scopedSlots: { customRender: 'ns' } },
        { title: '添加时间', key: 'created_at', dataIndex: 'created_at', width: 160,
          sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
          defaultSortOrder: 'descend',
          customRender: (t) => t || '—' },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: 250, align: 'right' },
      ],
    };
  },
  computed: {
    warnCount() {
      return this.items.filter(i => i.health_warn == 1).length;
    },
    displayItems() {
      const kw = (this.search || '').trim().toLowerCase();
      return this.items.filter(i => {
        if (this.onlyWarn && i.health_warn != 1) return false;
        if (this.filterStatus && i.status !== this.filterStatus) return false;
        if (this.filterCf === 'ready' && i.cf_ready !== '1') return false;
        if (this.filterCf === 'pending' && !(i.cf_zone_id && i.cf_ready !== '1')) return false;
        if (this.filterCf === 'none' && i.cf_zone_id) return false;
        if (this.filterValid && i.valid_state !== this.filterValid) return false;
        if (kw) {
          const hay = ((i.domain || '') + ' ' + (i.owner || '')).toLowerCase();
          if (hay.indexOf(kw) === -1) return false;
        }
        return true;
      });
    },
    stats() {
      const t = this.items.length;
      const cf = this.items.filter(i => i.cf_ready === '1').length;
      const pend = this.items.filter(i => i.status === 'pending_ns').length;
      const pool = this.items.filter(i => i.status === 'pool').length;
      return [
        { title: '域名总数', value: t, prefix: '', suffix: '', icon: IC.total, status: 'primary' },
        { title: '已接入 CF', value: cf, prefix: '', suffix: '', icon: IC.cf, status: 'success' },
        { title: '待改 NS', value: pend, prefix: '', suffix: '', icon: IC.pending, status: 'warning' },
        { title: '未分配', value: pool, prefix: '', suffix: '', icon: IC.pool, status: 'info' },
      ];
    },
  },
  created() {
    this.load();
    this._timer = setInterval(() => { this.load(); }, 60000); // 60s 自动刷新列表
  },
  beforeDestroy() { if (this._timer) clearInterval(this._timer); },
  methods: {
    rowClass(record) { return record.health_warn == 1 ? 'dm-row-warn' : ''; },
    statusLabel(s) {
      return ({ pending_ns: '待改NS', active: '已上线', assigned: '已分配',
        pool: '未分配', orphan: '待清理', creating_cf: '接入中', error: '错误' })[s] || (s || '-');
    },
    load() {
      this.loading = true;
      dmList('').then(res => {
        this.items = (res && res.data && res.data.items) ? res.data.items : [];
      }).catch(() => this.$message.error('加载失败')).finally(() => this.loading = false);
    },
    clearFilters() { this.search = ''; this.filterStatus = ''; this.filterCf = ''; this.filterValid = ''; this.onlyWarn = false; },
    doHealthCheck(domain) {
      this.checking = domain;
      dmHealthCheck({ domain }).then(res => {
        const d = (res && res.data) ? res.data : {};
        if (d.health === 'ok') this.$message.success(domain + ' 正常 (HTTP ' + d.health_code + ')');
        else if (d.health_warn == 1) this.$message.error(domain + ' 连续不可达 (HTTP ' + d.health_code + '),疑似到期');
        else this.$message.warning(domain + ' 本次不可达 (HTTP ' + d.health_code + '),将继续复检');
        this.load();
      }).catch(() => this.$message.error('检测失败')).finally(() => this.checking = '');
    },
    ttlText(ttl) {
      if (ttl === null || ttl === undefined) return '';
      if (ttl < 0) return '永久有效';
      const days = Math.floor(ttl / 86400);
      return days > 0 ? ('剩余约 ' + days + ' 天') : '今天到期';
    },
    doMigrate(domain) {
      if (!domain) { this.$message.warning('请输入域名'); return; }
      this.migrating = true;
      dmMigrate({ domain }).then(res => {
        if (res && res.status === 200 && res.data && res.data.name_servers) {
          this.nsDomain = res.data.domain; this.nsList = res.data.name_servers; this.nsVisible = true;
          this.newDomain = ''; this.load();
        } else {
          this.$message.error((res && res.data && res.data.errors) ? JSON.stringify(res.data.errors) : '迁移失败');
        }
      }).catch(() => this.$message.error('迁移失败(检查CF配置)')).finally(() => this.migrating = false);
    },
    doBatch() {
      const list = (this.batchText || '').split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      if (!list.length) { this.$message.warning('请输入至少一个域名'); return; }
      if (this.batchOwner && !/^[@A-Za-z0-9_.-]+$/.test(this.batchOwner)) { this.$message.warning('用户名格式不对'); return; }
      this.batching = true; this.batchResult = null;
      dmBatchMigrate({ domains: list, owner: this.batchOwner || '' }).then(res => {
        if (res && res.status === 200 && res.data) {
          this.batchResult = res.data;
          this.$message.success('已受理 ' + res.data.accepted_count + ' 个,后台处理中');
          this.batchText = '';
          setTimeout(() => this.load(), 3000);
        } else {
          this.$message.error((res && res.message) ? res.message : '批量提交失败');
        }
      }).catch((e) => this.$message.error((e && e.message) ? e.message : '批量提交失败(用户不存在?)'))
        .finally(() => this.batching = false);
    },
    doRefresh(domain) {
      dmRefresh({ domain }).then(res => {
        const st = res && res.data ? res.data.cf_status : '';
        this.$message.info('CF 状态: ' + st); this.load();
      }).catch(() => this.$message.error('刷新失败'));
    },
    openAssign(record) { this.assignDomain = record.domain; this.assignOwner = record.owner || ''; this.assignVisible = true; },
    doAssign() {
      if (!this.assignOwner) { this.$message.warning('请输入客户用户名'); return; }
      this.assigning = true;
      dmAssign({ domain: this.assignDomain, owner: this.assignOwner }).then(res => {
        if (res && res.status === 200) { this.$message.success('已分配'); this.assignVisible = false; this.load(); }
        else this.$message.error('分配失败(用户不存在?)');
      }).catch(() => this.$message.error('分配失败(用户不存在?)')).finally(() => this.assigning = false);
    },
    doUnassign(domain) {
      dmUnassign({ domain }).then(() => { this.$message.success('已取消分配'); this.load(); })
        .catch(() => this.$message.error('操作失败'));
    },
    doRemove(record, purgeCf) {
      const cf = !!purgeCf;
      const h = this.$createElement;
      this.$confirm({
        title: cf ? ('彻底删除 ' + record.domain + '?') : ('仅删本地 ' + record.domain + '?'),
        content: h('div', { style: 'max-width:320px' }, [
          h('p', { style: 'margin:0 0 6px' }, '将删除:源站目录 /www/wwwdomain、nginx 配置、注册表记录、归属客户的 website。'),
          cf
            ? h('p', { style: 'margin:0;color:#f5222d' }, '并会删除 Cloudflare zone(域名将脱离 CF)。此操作不可撤销(目录归档到备份)。')
            : h('p', { style: 'margin:0;color:#1890ff' }, '保留 Cloudflare zone(DNS 解析不动),之后可重新建站接入。目录归档到备份。'),
        ]),
        okText: cf ? '彻底删除' : '仅删本地',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          return dmRemove({ domain: record.domain, purge_cf: cf }).then(() => {
            this.$message.success(cf ? '已删除(含 CF zone)' : '已删除本地(CF zone 已保留)');
            this.load();
          }).catch(() => this.$message.error('删除失败'));
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dm-toolbar {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; flex-wrap: wrap;
}
.dm-ns-bar {
  margin: 0 24px 4px; border-radius: 8px; font-weight: 600; letter-spacing: .3px;
}
.dm-filters {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 12px 24px 4px;
}
.dm-count { color: #8c8c8c; font-size: 13px; margin-left: auto; }
.dm-table { padding: 8px 8px 16px; }
.dm-domain { display: flex; align-items: center; gap: 8px; }
.dm-dot { width: 8px; height: 8px; border-radius: 50%; background: #d9d9d9; flex: none; }
.dm-dot.dot-active { background: #52c41a; }
.dm-dot.dot-pending_ns { background: #fa8c16; }
.dm-dot.dot-assigned { background: #1890ff; }
.dm-dot.dot-orphan { background: #f5222d; }
.dm-name { font-weight: 600; color: #141414; }
.dm-owner { font-weight: 600; color: #1890ff; }
.dm-ns { font-size: 12px; color: #8c8c8c; font-family: monospace; }
.dm-del { color: #f5222d; }
.dm-warn-bar { border-radius: 8px; font-weight: 600; }
::v-deep .dm-row-warn > td { background: #fff1f0 !important; }
::v-deep .dm-row-warn:hover > td { background: #ffe4e1 !important; }
.tag-status { border: none; border-radius: 6px; font-weight: 600; }
.tag-status.st-active { background: #f6ffed; color: #52c41a; }
.tag-status.st-pending_ns { background: #fff7e6; color: #fa8c16; }
.tag-status.st-assigned { background: #e6f7ff; color: #1890ff; }
.tag-status.st-pool { background: #fafafa; color: #8c8c8c; }
.tag-status.st-orphan { background: #fff1f0; color: #f5222d; }
.tag-status.st-creating_cf { background: #f9f0ff; color: #722ed1; }
.dm-ns-list { display: flex; flex-direction: column; gap: 10px; align-items: center; }
.dm-ns-item { font-size: 15px; padding: 6px 16px; font-family: monospace; }
</style>
